import React, { useEffect, useRef, useState } from "react";
import { Alert, Modal, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import {
    aiErrorMessage,
    createOfferImport,
    getAiRun,
    IAiRun,
    IOfferImportPosition,
    isAiRunFinished,
} from "../actions/ai";
import { toCurrencyString } from "../utils";

export const OFFER_IMPORT_TYPES = ["application/pdf", "image/png", "image/jpeg"];
export const OFFER_IMPORT_MAX_SIZE = 20 * 1024 * 1024;
const POLL_INTERVAL_MS = 2000;
const MARKUP_STORAGE_KEY = "offerImportMarkup";

export interface IImportedOfferPosition {
    title: string;
    qty: number;
    price: number;
}

export const offerImportFileError = (file: File | null): string | null => {
    if (!file) {
        return "Bitte eine Datei auswählen";
    }
    if (!OFFER_IMPORT_TYPES.includes(file.type)) {
        return "Nur PDF, JPG oder PNG";
    }
    if (file.size > OFFER_IMPORT_MAX_SIZE) {
        return "Datei zu groß (max. 20 MB)";
    }
    return null;
};

export const parseMarkup = (value: string): number | null => {
    const normalized = String(value).replace(",", ".").trim();
    if (normalized === "") {
        return 0;
    }
    const markup = Number(normalized);
    return Number.isFinite(markup) && markup > -100 ? markup : null;
};

export const withMarkup = (cents: number, markup: number): number =>
    Math.round(cents * (1 + markup / 100));

export const importedOfferPositions = (
    positions: IOfferImportPosition[],
    markup: number
): IImportedOfferPosition[] =>
    positions.map((position) => ({
        title: position.title,
        qty: position.qty,
        price: withMarkup(position.unit_price_cents, markup),
    }));

const loadMarkup = (): string => {
    try {
        return localStorage.getItem(MARKUP_STORAGE_KEY) || "";
    } catch (e) {
        return "";
    }
};

const storeMarkup = (value: string) => {
    try {
        localStorage.setItem(MARKUP_STORAGE_KEY, value);
    } catch (e) {
        return;
    }
};

interface IModalOfferImport {
    show: boolean;
    onClose: () => void;
    onImport: (positions: IImportedOfferPosition[]) => void;
}

export const ModalOfferImport = ({ show, onClose, onImport }: IModalOfferImport) => {
    const [file, setFile] = useState<File | null>(null);
    const [fileError, setFileError] = useState<string | null>(null);
    const [run, setRun] = useState<IAiRun | null>(null);
    const [busy, setBusy] = useState(false);
    const [markupInput, setMarkupInput] = useState(loadMarkup);
    const session = useRef(0);

    useEffect(() => {
        session.current += 1;
        setFile(null);
        setFileError(null);
        setRun(null);
        setBusy(false);
        setMarkupInput(loadMarkup());
    }, [show]);

    useEffect(() => {
        if (!show || !run || isAiRunFinished(run)) {
            return;
        }
        const current = session.current;
        const timer = setInterval(async () => {
            try {
                const next = await getAiRun(run.id);
                if (current === session.current) {
                    setRun(next);
                }
            } catch (err) {
                if (current === session.current) {
                    clearInterval(timer);
                    toast.error(aiErrorMessage(err, "Status konnte nicht geladen werden"));
                }
            }
        }, POLL_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [show, run]);

    const handleFile = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const selected = ev.target.files && ev.target.files[0] ? ev.target.files[0] : null;
        setFile(selected);
        setFileError(offerImportFileError(selected));
    };

    const start = async () => {
        const error = offerImportFileError(file);
        setFileError(error);
        if (error || !file) {
            return;
        }
        setBusy(true);
        const current = session.current;
        try {
            const created = await createOfferImport(file);
            if (current === session.current) {
                setRun(created);
            }
        } catch (err) {
            if (current === session.current) {
                toast.error(aiErrorMessage(err, "Import konnte nicht gestartet werden"));
            }
        } finally {
            if (current === session.current) {
                setBusy(false);
            }
        }
    };

    const markup = parseMarkup(markupInput);
    const positions = run?.output?.positions || [];
    const finished = isAiRunFinished(run);
    const output = run?.output || {};

    const apply = () => {
        if (markup === null || positions.length === 0) {
            return;
        }
        storeMarkup(markupInput);
        onImport(importedOfferPositions(positions, markup));
        onClose();
    };

    return (
        <Modal show={show} onHide={onClose} size="lg" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Aus Herstellerangebot importieren</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {!run && (
                    <>
                        <p className="text-muted">
                            Herstellerangebot als PDF, JPG oder PNG hochladen. Die KI liest die
                            Positionen mit Menge und Einkaufspreis aus. Mit dem Aufschlag werden
                            daraus Verkaufspreise, die danach im Angebot frei editierbar sind.
                        </p>
                        <div className="form-group">
                            <label htmlFor="offer-import-file">Datei</label>
                            <input
                                id="offer-import-file"
                                type="file"
                                className="form-control-file"
                                accept=".pdf,image/png,image/jpeg"
                                onChange={handleFile}
                                disabled={busy}
                            />
                        </div>
                        {fileError && <Alert variant="danger">{fileError}</Alert>}
                    </>
                )}
                {run && !finished && (
                    <div className="text-center" style={{ padding: "40px 0" }}>
                        <Spinner animation="border" />
                        <p className="text-muted" style={{ marginTop: "12px" }}>
                            Angebot wird gelesen… das kann bis zu zwei Minuten dauern.
                        </p>
                    </div>
                )}
                {run && finished && run.status === "failed" && (
                    <Alert variant="danger">{run.error || "Import fehlgeschlagen"}</Alert>
                )}
                {run && finished && run.status === "succeeded" && (
                    <>
                        {(output.supplier || output.offer_number) && (
                            <p>
                                <strong>{output.supplier}</strong>
                                {output.offer_number ? ` · Angebot ${output.offer_number}` : ""}
                            </p>
                        )}
                        {output.sum_matches === false && (
                            <Alert variant="warning">
                                Die Summe der Positionen (
                                {toCurrencyString(output.positions_total_cents || 0)}) weicht von
                                der Angebotssumme ({toCurrencyString(output.net_total_cents || 0)})
                                ab. Bitte prüfen.
                            </Alert>
                        )}
                        {output.sum_matches == null && (
                            <Alert variant="warning">
                                Keine Angebotssumme erkannt, Positionen bitte prüfen.
                            </Alert>
                        )}
                        {(output.truncated_pages || 0) > 0 && (
                            <Alert variant="warning">
                                Nur die ersten Seiten wurden gelesen, {output.truncated_pages}{" "}
                                Seite(n) fehlen.
                            </Alert>
                        )}
                        <div className="form-group" style={{ maxWidth: "200px" }}>
                            <label htmlFor="offer-import-markup">Aufschlag in %</label>
                            <input
                                id="offer-import-markup"
                                type="text"
                                inputMode="decimal"
                                className={`form-control${markup === null ? " is-invalid" : ""}`}
                                value={markupInput}
                                placeholder="0"
                                onChange={(ev) => setMarkupInput(ev.target.value)}
                            />
                        </div>
                        <Table size="sm" striped>
                            <thead>
                                <tr>
                                    <th>Position</th>
                                    <th className="text-right">Menge</th>
                                    <th className="text-right">EK</th>
                                    <th className="text-right">VK</th>
                                </tr>
                            </thead>
                            <tbody>
                                {positions.map((position, index) => (
                                    <tr key={index}>
                                        <td>{position.title}</td>
                                        <td className="text-right">
                                            {String(position.qty).replace(".", ",")}
                                        </td>
                                        <td className="text-right">
                                            {toCurrencyString(position.unit_price_cents)}
                                        </td>
                                        <td className="text-right">
                                            {markup === null
                                                ? "–"
                                                : toCurrencyString(
                                                      withMarkup(position.unit_price_cents, markup)
                                                  )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onClose}
                    disabled={busy}
                >
                    Schließen
                </button>
                {!run && (
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={start}
                        disabled={busy || !file || !!fileError}
                    >
                        {busy ? "Wird hochgeladen…" : "Import starten"}
                    </button>
                )}
                {run && finished && run.status === "succeeded" && (
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={apply}
                        disabled={markup === null || positions.length === 0}
                    >
                        {positions.length} Position(en) übernehmen
                    </button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default ModalOfferImport;
